import CustomButton from 'Components/CustomButton'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import SPMActivityDialog from 'Components/SPMActivityDialog'
import type { ActivityData } from 'Components/SPMActivityDialog/SPMActivityDialog'
import SPMActivityDialogButton from 'Components/SPMActivityDialogButton'
import { WithLoader } from 'Components/WithLoader'
import SPMAllowedApiClients from 'Services/ServiceProviderManagement/Features/AllowedClient/Components/SPMAllowedApiClients'
import ServiceProviderDetails from 'Services/ServiceProviderManagement/Features/ServiceProvider/Components/ServiceProviderDetails'
import ServiceProviderSalesPoints from 'Services/ServiceProviderManagement/Features/ServiceProvider/Components/ServiceProviderSalesPoints'
import { SELECTED_SERVICE_PROVIDER_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/ServiceProvider/Hooks/useServiceProvidersWithParams'
import { SERVICE_PROVIDER_DETAILS_DRAWER_TAB_PARAM_KEY } from 'Services/ServiceProviderManagement/Features/ServiceProvider/Pages/ServiceProviderListPage'
import {
  useGetSingleServiceProviderQuery,
  useUpdateSingleServiceProviderMutation
} from 'Services/ServiceProviderManagement/Features/ServiceProvider/serviceProviderApi'
import {
  type ServiceProvider,
  ServiceProviderTabType
} from 'Services/ServiceProviderManagement/Features/ServiceProvider/types'
import { CopyObjectKeys, useCopyObjectContext } from 'Util/Context/copyObjectContext'
import { ENABLE_SPM_DELETE_BUTTON, SERVICE_PROVIDER_MANAGEMENT_ROOT_URL } from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import SnackbarUtils from 'Util/Snackbar'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Button, Divider, Tab } from '@mui/material'
import { Box } from '@mui/system'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

interface ServiceProviderDrawerProps {
  open: boolean
  handleClose: () => void
  handleServiceProviderDelete: () => void
}

export default function ServiceProviderDrawer({
  open,
  handleClose,
  handleServiceProviderDelete
}: Readonly<ServiceProviderDrawerProps>) {
  const baseUrl = `${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/service-providers`
  const navigate = useNavigate()
  const { confirm } = useConfirm()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activityDialogOpen, setActivityDialogOpen] = useState(false)
  const serviceProviderDetailsDrawerTabFromParams =
    searchParams.get(SERVICE_PROVIDER_DETAILS_DRAWER_TAB_PARAM_KEY) ??
    ServiceProviderTabType.DETAILS
  const { copyObject } = useCopyObjectContext()

  const selectedServiceProviderId = searchParams.get(SELECTED_SERVICE_PROVIDER_PARAMETER_KEY)

  const { data: singleServiceProvider, isFetching: isSingleServiceProviderFetching } =
    useGetSingleServiceProviderQuery(selectedServiceProviderId ?? '', {
      skip: !selectedServiceProviderId
    })

  const [updateServiceProvider, { isLoading: isSubmitLoading }] =
    useUpdateSingleServiceProviderMutation()

  const [selectedReadClients, setSelectedReadClients] = useState<string[]>([])
  const [selectedWriteClients, setSelectedWriteClients] = useState<string[]>([])

  // Sync local client state with server data during render (not in useEffect)
  // so the transfer list never mounts with stale values.
  const [prevServiceProvider, setPrevServiceProvider] = useState<ServiceProvider | undefined>(
    undefined
  )

  // Reset tracking when drawer closes so reopening re-syncs
  if (!open && prevServiceProvider !== undefined) {
    setPrevServiceProvider(undefined)
  }

  if (open && singleServiceProvider && singleServiceProvider !== prevServiceProvider) {
    setPrevServiceProvider(singleServiceProvider)
    setSelectedReadClients(singleServiceProvider.client_read ?? [])
    setSelectedWriteClients(singleServiceProvider.client_write ?? [])
  }

  const handleServiceProviderDetailsDrawerTabChange = (
    _event: React.SyntheticEvent,
    newValue: ServiceProviderTabType
  ) => {
    searchParams.set(SERVICE_PROVIDER_DETAILS_DRAWER_TAB_PARAM_KEY, newValue)
    setSearchParams(searchParams)
  }

  const handleActivityDialogOpen = () => {
    setActivityDialogOpen(true)
  }

  const handleActivityDialogClose = () => {
    setActivityDialogOpen(false)
  }

  const handleActivityDialogSubmit = (
    e: React.FormEvent<HTMLFormElement>,
    activityData: ActivityData
  ) => {
    e.preventDefault()
    const serviceProviderData = {
      ...singleServiceProvider,
      ...activityData
    } as ServiceProvider

    updateServiceProvider({
      ...serviceProviderData,
      id: serviceProviderData.service_provider_id
    })
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Service provider successfully saved.`)
        handleActivityDialogClose()
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error saving the service provider.`)
      })
  }

  const handleAllowedApiClientsSave = () => {
    if (!singleServiceProvider) return

    const serviceProviderData = {
      ...singleServiceProvider,
      client_read: selectedReadClients,
      client_write: selectedWriteClients
    }

    updateServiceProvider({
      ...serviceProviderData,
      id: singleServiceProvider.service_provider_id
    })
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Service provider successfully saved.`)
        const selectedServiceProviderParam = `${SELECTED_SERVICE_PROVIDER_PARAMETER_KEY}=${singleServiceProvider.service_provider_id}`
        const detailsDrawerTabParam = `${SERVICE_PROVIDER_DETAILS_DRAWER_TAB_PARAM_KEY}=${ServiceProviderTabType.ALLOWED_API_CLIENTS}`
        navigate(`${baseUrl}/?${selectedServiceProviderParam}&${detailsDrawerTabParam}`)
      })
      .catch(() => SnackbarUtils.error(t`There was an error saving the service provider.`))
  }

  const copyDataAndNavigateToCreate = () => {
    if (!singleServiceProvider) return

    copyObject(CopyObjectKeys.SERVICE_PROVIDER, singleServiceProvider, ['service_provider_id'])
    navigate(`${baseUrl}/create`)
  }

  const isLoading = isSingleServiceProviderFetching

  return (
    <DrawerRight
      open={open}
      onClose={handleClose}
      topBar={
        <Box>
          {singleServiceProvider && (
            <SPMActivityDialogButton
              activityEndDate={singleServiceProvider.activity_end_date?.toString()}
              isActive={singleServiceProvider.active}
              isLoading={isSubmitLoading}
              onClick={handleActivityDialogOpen}
              sx={{ mr: 1 }}
            />
          )}
          {ENABLE_SPM_DELETE_BUTTON && (
            <DeleteButton
              sx={{ mr: 1 }}
              size="small"
              onClick={() =>
                confirm({
                  title: t`Are you sure?`,
                  message: t`Are you sure you want to delete the service provider?`,
                  handleConfirm: () => {
                    handleServiceProviderDelete()
                  }
                })
              }
            >{t`Delete`}</DeleteButton>
          )}
          <CustomButton
            sx={{ mr: 1 }}
            onClick={() =>
              navigate(`${baseUrl}/${singleServiceProvider?.service_provider_id}/edit`)
            }
            size="small"
            startIcon={<EditIcon />}
          >{t`Edit`}</CustomButton>
          {singleServiceProvider && (
            <CustomButton
              onClick={copyDataAndNavigateToCreate}
              size="small"
              startIcon={<ContentCopyIcon />}
            >{t`Use as template`}</CustomButton>
          )}
        </Box>
      }
    >
      <WithLoader loading={isLoading}>
        {singleServiceProvider && (
          <TabContext value={serviceProviderDetailsDrawerTabFromParams}>
            <TabList
              aria-label={t`Service provider tab list`}
              onChange={handleServiceProviderDetailsDrawerTabChange}
            >
              <Tab label={t`Details`} value={ServiceProviderTabType.DETAILS} />
              <Tab label={t`Sales points`} value={ServiceProviderTabType.SALES_POINTS} />
              <Tab
                label={t`Allowed API clients`}
                value={ServiceProviderTabType.ALLOWED_API_CLIENTS}
              />
            </TabList>
            <Divider />
            <TabPanel sx={{ p: 0 }} value={ServiceProviderTabType.DETAILS}>
              <ServiceProviderDetails serviceProvider={singleServiceProvider} />
            </TabPanel>
            <TabPanel sx={{ p: 0 }} value={ServiceProviderTabType.SALES_POINTS}>
              <ServiceProviderSalesPoints serviceProvider={singleServiceProvider} />
            </TabPanel>
            <TabPanel sx={{ p: 0 }} value={ServiceProviderTabType.ALLOWED_API_CLIENTS}>
              <SPMAllowedApiClients
                selectedReadClients={selectedReadClients}
                selectedWriteClients={selectedWriteClients}
                setSelectedReadClients={setSelectedReadClients}
                setSelectedWriteClients={setSelectedWriteClients}
              />
              <Box
                sx={{
                  mt: 2,
                  mx: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  columnGap: 2
                }}
              >
                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    color="inherit"
                    sx={{ width: '100%' }}
                  >
                    {t`Cancel`}
                  </Button>
                  <CustomLoadingButton
                    onClick={handleAllowedApiClientsSave}
                    isLoading={isSubmitLoading}
                    sx={{ width: '100%' }}
                    icon={<SaveIcon />}
                    iconPosition="start"
                  >
                    {t`Save changes`}
                  </CustomLoadingButton>
                </Box>
              </Box>
            </TabPanel>
          </TabContext>
        )}
      </WithLoader>
      {singleServiceProvider && (
        <SPMActivityDialog
          open={activityDialogOpen}
          handleClose={handleActivityDialogClose}
          originalActivityEndDate={singleServiceProvider.activity_end_date?.toString()}
          isActive={singleServiceProvider.active}
          onSubmit={handleActivityDialogSubmit}
          spmObjectName={t`service provider`}
          isSubmitLoading={isSubmitLoading}
        />
      )}
    </DrawerRight>
  )
}
